import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, PageLoader } from '@/components/atoms';
import { PageHeader, DataTable } from '@/components/molecules';
import { cn } from '@/lib/utils';
import type { ColumnDef } from '@tanstack/react-table';
import {
  EmptyState,
  Pagination,
  ReceiptSheet,
  ShareModal,
  SalesFilters,
} from './components';
import {
  fetchOrders,
  fetchOrder,
  ORDERS_QUERY_KEY,
  ORDER_QUERY_KEY,
} from '@/api/queries/sales';
import type { Sale, Contact, Order } from '@/types';
import { transformOrderToSale } from './utils/orderToSale';
import { getFilterDateFromPeriod } from '@/lib/helpers/format-date';

const EMPTY_ORDERS: Order[] = [];

// Mock contact data
const contactData: Contact[] = [
  {
    id: '1',
    name: 'Chris Friedkly',
    initials: 'CF',
    channel: 'whatsapp',
    lastMessage: 'I need a body oil how much?',
    time: '2 min ago',
  },
  {
    id: '2',
    name: 'Maggie Johnson',
    initials: 'MJ',
    channel: 'whatsapp',
    lastMessage: 'I need a Nike shoe size 45.',
    time: '5 min ago',
  },
  {
    id: '3',
    name: 'Gael Harry',
    initials: 'GH',
    channel: 'instagram',
    lastMessage: 'New York Finest Fruits',
    time: '1 hour ago',
  },
  {
    id: '4',
    name: 'Jenna Sullivan',
    initials: 'JS',
    channel: 'facebook',
    lastMessage: 'Walmart',
    time: '2 hours ago',
  },
  {
    id: '5',
    name: 'Alice Smith',
    initials: 'AS',
    channel: 'whatsapp',
    lastMessage: 'Thanks for the quick response!',
    time: '30 min ago',
  },
  {
    id: '6',
    name: 'Bob Wilson',
    initials: 'BW',
    channel: 'instagram',
    lastMessage: 'I will think about it',
    time: '3 hours ago',
  },
];

export const SalesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('today');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: ordersPage, isLoading } = useQuery({
    queryKey: [ORDERS_QUERY_KEY, currentPage],
    queryFn: () => fetchOrders({ page: currentPage }),
  });

  const { data: selectedOrder } = useQuery({
    queryKey: [ORDER_QUERY_KEY, selectedOrderId],
    queryFn: () => fetchOrder(selectedOrderId!),
    enabled: !!selectedOrderId,
  });

  const orders = ordersPage?.data ?? EMPTY_ORDERS;

  const salesDataWithOrders = useMemo(() => {
    if (!orders || !Array.isArray(orders)) return [];
    return orders.map((order) => ({
      sale: transformOrderToSale(order),
      originalOrder: order,
    }));
  }, [orders]);

  const showEmptyState =
    !isLoading && (!orders || !Array.isArray(orders) || orders.length === 0);

  const handleRowClick = (sale: Sale) => {
    setSelectedSale(sale);
    setSelectedOrderId(sale.id);
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedSale(null);
    setSelectedOrderId(null);
  };

  const handleShareReceipt = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const handleContactSelect = (contact: Contact) => {
    alert(`Receipt shared to ${contact.name} via ${contact.channel}!`);
    setShowShareModal(false);
  };

  const filteredData = useMemo(() => {
    let filtered = salesDataWithOrders;

    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
          item.sale.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.sale.products.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(
        (item) => item.sale.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (dateRange !== 'all') {
      const filterDate = getFilterDateFromPeriod(dateRange);

      filtered = filtered.filter((item) => {
        if (!item.originalOrder.createdAt) return false;
        const saleDate = new Date(item.originalOrder.createdAt);
        return saleDate >= filterDate;
      });
    }

    return filtered.map((item) => item.sale);
  }, [salesDataWithOrders, searchQuery, statusFilter, dateRange]);

  const columns: ColumnDef<Sale>[] = [
    {
      accessorKey: 'name',
      header: 'Customer',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'products',
      header: 'Products',
      cell: ({ row }) => <div>{row.getValue('products')}</div>,
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('amount')}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        return <span className="text-sm">{status}</span>;
      },
    },
    {
      accessorKey: 'payment',
      header: 'Payment',
      cell: ({ row }) => {
        const payment = row.getValue('payment') as string;
        return (
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                payment === 'Paid' ? 'bg-green-500' : 'bg-gray-400'
              )}
            />
            <span
              className={cn(
                payment === 'Paid' ? 'text-green-500' : 'text-gray-500'
              )}
            >
              {payment}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">{row.getValue('date')}</div>
      ),
    },
    {
      id: 'actions',
      header: 'VIEW',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleRowClick(row.original)}
          className="text-xs"
        >
          View
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          heading="All orders"
          description="Have all your channel in one place and make sales easy"
        />
        <PageLoader message="Loading sales..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        heading="All orders"
        description="Have all your channel in one place and make sales easy"
      />

      <SalesFilters
        dateRange={dateRange}
        statusFilter={statusFilter}
        searchQuery={searchQuery}
        onDateRangeChange={setDateRange}
        onStatusFilterChange={setStatusFilter}
        onSearchChange={setSearchQuery}
      />

      {showEmptyState ? (
        <EmptyState />
      ) : (
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={handleRowClick}
        />
      )}

      {!showEmptyState && (
        <Pagination
          currentPage={ordersPage?.currentPage ?? 1}
          lastPage={ordersPage?.lastPage ?? 1}
          onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
          onNext={() =>
            setCurrentPage((p) => Math.min(ordersPage?.lastPage ?? 1, p + 1))
          }
        />
      )}

      <ReceiptSheet
        showReceipt={showReceipt}
        setShowReceipt={setShowReceipt}
        selectedSale={selectedSale}
        selectedOrder={selectedOrder ?? null}
        onClose={handleCloseReceipt}
        onShare={handleShareReceipt}
      />

      <ShareModal
        showShareModal={showShareModal}
        setShowShareModal={setShowShareModal}
        onClose={handleCloseShareModal}
        onContactSelect={handleContactSelect}
        contacts={contactData}
      />
    </div>
  );
};
